// i18next-extract-mark-ns-start technology

import * as React from 'react'
import Layout from 'src/components/layout'
import { graphql } from 'gatsby'
import TechnologyArea, { ITechnologyAreaProps } from 'src/components/sections/technology/TechnologyArea'
import TechnologyAreaHeader from 'src/components/sections/technology/TechnologyAreaHeader'
import { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { gsap, ScrollTrigger } from 'gsap/all'

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger)
}
import { createShowFromBottomAnimation } from 'src/helpers/animations'
import FourHeadedItemsGrid from 'src/components/ui/FourHeadedItemsGrid'
import { isMdScreen } from 'src/helpers/layout'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import emphasizeAmpersand from 'src/helpers/emphasizeAmpersand'
import HeroSection from 'src/components/sections/hero'
import ContactSection from 'src/components/sections/ContactSection'
import { InteractiveRadar } from 'src/components/sections/technology/InteractiveRadar'
import { Link } from 'gatsby-plugin-react-i18next'
import ButtonIcon, { ButtonSizes } from 'src/components/ui/ButtonIcon'
import { ArrowRightIcon } from 'src/components/ui/SectionHeader'
import Subtitle from 'src/components/ui/Subtitle'
import fallbackRadar from '../../content/technology-radar.json'

interface StickyCategoryHeaderProps {
  title: string
}

const StickyCategoryHeader: React.FC<StickyCategoryHeaderProps> = ({ title }) => {
  const headerRef = useRef<HTMLDivElement>(null)
  const [isStuck, setIsStuck] = useState(false)

  useEffect(() => {
    const checkSticky = () => {
      const el = headerRef.current
      if (!el || !el.parentElement) return
      const parentRect = el.parentElement.getBoundingClientRect()
      setIsStuck(parentRect.top <= 1 && parentRect.bottom > 144)
    }

    checkSticky()
    window.addEventListener('scroll', checkSticky, { passive: true })
    window.addEventListener('resize', checkSticky, { passive: true })

    const lenis = typeof window !== 'undefined' ? (window as any).lenis : null
    if (lenis) {
      lenis.on('scroll', checkSticky)
    }

    return () => {
      window.removeEventListener('scroll', checkSticky)
      window.removeEventListener('resize', checkSticky)
      if (lenis) {
        lenis.off('scroll', checkSticky)
      }
    }
  }, [])

  return (
    <>
      <div
        className={`sticky top-0 z-10 -mx-4 md:-mx-6 px-4 md:px-6 h-[120px] md:h-[144px] -mb-[120px] md:-mb-[144px] pointer-events-none transition-colors duration-200 ${
          isStuck ? 'bg-surface-background/85 backdrop-blur-md' : 'bg-transparent'
        }`}
        aria-hidden="true"
      />
      <div
        ref={headerRef}
        className="sticky top-0 z-30 -mx-4 md:-mx-6 px-4 md:px-6 pt-4 md:pt-6 h-[72px] md:h-[88px] flex items-center bg-transparent"
      >
        <h4 className="font-palanquin-dark text-2xl sm:text-3xl md:text-4xl font-bold tracking-tighter text-on-surface leading-normal pb-1.5 whitespace-nowrap overflow-visible w-full">
          {emphasizeAmpersand(title, 1)}
        </h4>
      </div>
    </>
  )
}

interface StickySubcategoryHeaderProps {
  ring: string
}

const StickySubcategoryHeader: React.FC<StickySubcategoryHeaderProps> = ({ ring }) => {
  const { t } = useTranslation('technology')
  const headerRef = useRef<HTMLDivElement>(null)
  const [isLeaving, setIsLeaving] = useState(false)

  useEffect(() => {
    const checkLeaving = () => {
      const el = headerRef.current
      if (!el || !el.parentElement) return
      const parentRect = el.parentElement.getBoundingClientRect()
      const threshold = window.innerWidth >= 768 ? 88 : 72
      setIsLeaving(parentRect.bottom <= threshold + 48)
    }

    checkLeaving()
    window.addEventListener('scroll', checkLeaving, { passive: true })
    window.addEventListener('resize', checkLeaving, { passive: true })

    const lenis = typeof window !== 'undefined' ? (window as any).lenis : null
    if (lenis) {
      lenis.on('scroll', checkLeaving)
    }

    return () => {
      window.removeEventListener('scroll', checkLeaving)
      window.removeEventListener('resize', checkLeaving)
      if (lenis) {
        lenis.off('scroll', checkLeaving)
      }
    }
  }, [])

  return (
    <div
      ref={headerRef}
      className={`sticky top-[72px] md:top-[88px] z-20 -mx-4 md:-mx-6 px-4 md:px-6 h-12 md:h-14 mb-6 md:mb-8 flex items-center bg-transparent transition-opacity duration-150 ${
        isLeaving ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}
    >
      <Subtitle className="mb-0 md:mb-0">
        {t(ring, { defaultValue: ring })}
      </Subtitle>
    </div>
  )
}

const IndexPage = ({ data }: any) => {
  const { t } = useTranslation('technology')

  const getEmptyRings = () => ({ adopt: [], trial: [], assess: [], hold: [] })
  const tAreas = t('technology_areas', { returnObjects: true })
  const tBlips = t('blip', { returnObjects: true })
  const technologyAreas: any[] =
    Array.isArray(tAreas) && tAreas.length > 0
      ? tAreas
      : fallbackRadar.technology_areas || []
  const blips: any[] =
    Array.isArray(tBlips) && tBlips.length > 0
      ? tBlips
      : fallbackRadar.blip || []

  const mappedTechnologies = blips.map(blip => {
    const area = technologyAreas.find(a => a.id === blip.technologyArea?.id)
    return {
      id: blip.id,
      name: blip.name,
      category: area ? area.translations[0]?.title || 'Inne' : 'Inne',
      ring: blip.ring,
      description: blip.translations?.[0]?.description?.replace(/<[^>]*>?/gm, ' ').replace(/&nbsp;/g, ' ').replace(/[\n\r]+/g, ' ') || '',
      status: 'Opublikowano',
    }
  })

  const areas: ITechnologyAreaProps = technologyAreas.reduce(
    (allAreas, area) => ({ ...allAreas, [area.id]: getEmptyRings() }),
    {}
  )

  blips.forEach((technology) => {
    if (areas[technology.technologyArea?.id]) {
      areas[technology.technologyArea.id][technology.ring].push(technology)
    }
  })

  const [openAreaId, setOpenAreaId] = useState<number | null>(
    technologyAreas.length > 0 ? technologyAreas[0].id : null
  )
  const [openTechnologyId, setOpenTechnologyId] = useState<number | null>(null)
  const [showNewLayout, setShowNewLayout] = useState(true)
  const [showInteractiveView, setShowInteractiveView] = useState(true)
  const [isDesktopRadar, setIsDesktopRadar] = useState(false)

  const timeline = useRef<any>(null)
  const effectiveShowInteractive = isDesktopRadar && showInteractiveView

  useEffect(() => {
    if (typeof window !== 'undefined' && showNewLayout && !showInteractiveView) {
      // Kill existing triggers for reveal items to prevent duplicates and stale states
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.trigger && (trigger.trigger as Element).classList.contains('reveal-item')) {
          trigger.kill();
        }
      });

      const tl = gsap.timeline()

      const timeout = setTimeout(() => {
        const elements = document.querySelectorAll('.reveal-item')
        elements.forEach((el) => {
          const rect = el.getBoundingClientRect();
          const isInViewport = rect.top < (window.innerHeight || document.documentElement.clientHeight) && rect.bottom > 0;

          if (isInViewport) {
            gsap.set(el, { y: 0, opacity: 1 });
          } else {
            createShowFromBottomAnimation(tl, el);
          }
        })

        // Force ScrollTrigger to recalculate positions
        ScrollTrigger.refresh()
      }, 100)

      return () => {
        clearTimeout(timeout)
        tl.kill()
      }
    }
  }, [showNewLayout, showInteractiveView])

  useEffect(() => {
    const handleResize = () => {
      setIsDesktopRadar(window.innerWidth >= 1024)
    }
    // Uruchomienie przy montowaniu
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined' || effectiveShowInteractive) return

    const updateMaxHeight = () => {
      const rows = document.querySelectorAll<HTMLElement>('.radar-interactive-row')
      if (!rows.length) return

      rows.forEach(row => {
        row.style.minHeight = ''
      })

      if (window.innerWidth < 768) return

      let maxHeight = 0
      rows.forEach(row => {
        const h = row.offsetHeight
        if (h > maxHeight) maxHeight = h
      })

      if (maxHeight > 0) {
        rows.forEach(row => {
          row.style.minHeight = `${maxHeight}px`
        })
      }
    }

    updateMaxHeight()
    const timeout = setTimeout(updateMaxHeight, 150)
    if (document.fonts?.ready) {
      document.fonts.ready.then(updateMaxHeight)
    }

    window.addEventListener('resize', updateMaxHeight, { passive: true })
    return () => {
      clearTimeout(timeout)
      window.removeEventListener('resize', updateMaxHeight)
    }
  }, [effectiveShowInteractive, data])

  const onAreaHeaderClick = (areaId: number) => {
    if (isMdScreen()) {
      return
    }
    setOpenAreaId(openAreaId === areaId ? null : areaId)
  }

  const onTechnologyClick = (technologyId: number, areaId: number) => {
    setOpenTechnologyId(
      openTechnologyId === technologyId ? null : technologyId
    )
    setOpenAreaId(areaId)
  }

  const getBodyKey = (areaId: number) => `area-body-${areaId}`
  const getHeaderKey = (areaId: number) => `area-header-${areaId}`

  const getHeader = (area: any) => (
    <TechnologyAreaHeader
      key={getHeaderKey(area.id)}
      title={area.translations[0]?.title || ''}
      open={openAreaId === area.id}
      onClick={() => onAreaHeaderClick(area.id)}
    />
  )

  const getBody = (area: any) => (
    <TechnologyArea
      key={getBodyKey(area.id)}
      rings={areas[area.id]}
      openTechnologyId={openTechnologyId?.toString() || undefined}
      onTechnologyClick={(technologyId) =>
        onTechnologyClick(Number(technologyId), area.id)
      }
      className={
        openAreaId === area.id
          ? ''
          : 'overflow-hidden md:overflow-visible h-0 md:h-auto'
      }
    />
  )

  return (
    <Layout
      title={t('page-title')}
      metas={[{ name: 'description', content: t('Technology radar') }]}
      isBottomMenuHidden={false}
    >
      <HeroSection
        translationContent={{
          subtitle: '',
          firstPartOfTitle: 'me&my friends',
          secondPartOfTitle: t('technology radar'),
          content: '',
          ctaLink: '',
          ctaText: '',
          sectionName: 'technology-radar-hero',
        }}
        isFullHeight={false}
        showIso={false}
        sectionClassName="mt-12 md:mt-colossal"
        isH2={false}
        beforeTitleContent={
          <Link to="/" className="inline-block">
            <ButtonIcon
              size={ButtonSizes.large}
              morphingIcon="arrow-to-x"
            />
          </Link>
        }
      />
      <div className="container pb-10 md:pb-20">
        {isDesktopRadar && (
          <div className="hidden lg:flex justify-end mb-8 md:mb-12">
            <button
              type="button"
              onClick={() => setShowInteractiveView((prev) => !prev)}
              className="group relative inline-flex items-center text-title-large md:text-headline-small font-palanquin font-semibold text-on-surface select-none cursor-pointer"
            >
              <span
                className="absolute left-0 inline-flex items-center text-primary opacity-0 -translate-x-4 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-700 ease-[cubic-bezier(0.65,0,0.35,1)] shrink-0 pointer-events-none"
                aria-hidden="true"
              >
                <ArrowRightIcon className="w-5 h-5 md:w-6 md:h-6" />
              </span>
              <span className="inline-block border-b border-dashed border-primary pb-0.5 transition-transform duration-700 ease-[cubic-bezier(0.65,0,0.35,1)] group-hover:translate-x-8 md:group-hover:translate-x-9">
                {showInteractiveView
                  ? t('list view', { defaultValue: 'list view' })
                  : t('interactive radar', { defaultValue: 'interactive radar' })}
              </span>
            </button>
          </div>
        )}

        {!effectiveShowInteractive ? (
          <motion.div
            key="list-view"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: [0.2, 1, 0.3, 1] }}
            className="w-full"
          >
            <div className="flex flex-col gap-20 md:gap-40">
              {technologyAreas.length > 0 ? (
                technologyAreas.map((area) => {
                  const ringsToRender = ['adopt', 'trial', 'assess', 'hold'];
                  const hasAnyBlips = ringsToRender.some(ring => areas[area.id][ring]?.length > 0);

                  if (!hasAnyBlips) return null;

                  return (
                    <div key={area.id} className="flex flex-col relative">
                      {/* Sticky Category Header */}
                      <StickyCategoryHeader title={area.translations[0]?.title || ''} />

                      <div className="flex flex-col gap-16 md:gap-24">
                        {ringsToRender.map(ring => {
                          const blipsInRing = areas[area.id][ring];
                          if (!blipsInRing || blipsInRing.length === 0) return null;
                          return (
                            <div key={ring} className="flex flex-col relative">
                              {/* Sticky Subcategory Header */}
                              <StickySubcategoryHeader ring={ring} />

                              <ul className="w-full border-t border-dashed border-outline-lower flex flex-col">
                                {blipsInRing.map((blip, index) => {
                                  const itemTitle = blip.name
                                  const itemText = blip.translations?.[0]?.description?.replace(/<[^>]*>?/gm, ' ').replace(/&nbsp;/g, ' ').replace(/[\n\r]+/g, ' ') || ''
                                  return (
                                    <li
                                      key={blip.id}
                                      tabIndex={0}
                                      className="radar-interactive-row group w-full border-b border-dashed border-outline-lower py-5 sm:py-6 md:py-7 md:min-h-[104px] px-0 md:px-6 grid grid-cols-1 md:grid-cols-12 gap-1 md:gap-8 items-center cursor-default focus:outline-none"
                                    >
                                      <div className="col-span-1 md:col-span-4 flex items-center md:gap-6 min-w-0 w-full">
                                        <span
                                          className="hidden md:flex text-primary w-0 overflow-hidden opacity-0 -translate-x-4 md:group-hover:w-6 sm:md:group-hover:w-7 md:group-hover:w-8 md:group-focus-visible:w-6 md:group-hover:opacity-100 md:group-hover:translate-x-0 md:group-focus-visible:opacity-100 md:group-focus-visible:translate-x-0 transition-all duration-700 ease-[cubic-bezier(0.65,0,0.35,1)] shrink-0 items-center"
                                          aria-hidden="true"
                                        >
                                          <svg
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 shrink-0"
                                          >
                                            <line x1="3" y1="12" x2="21" y2="12" />
                                            <polyline points="15 6 21 12 15 18" />
                                          </svg>
                                        </span>
                                        <div className="flex items-start justify-between md:justify-start md:gap-3 w-full md:transition-transform md:duration-700 md:ease-[cubic-bezier(0.65,0,0.35,1)] md:group-hover:translate-x-2 md:group-focus-visible:translate-x-2 min-w-0 py-0 md:py-1">
                                          <span className="font-palanquin-dark font-normal text-title-medium sm:text-title-large md:text-headline-small lg:text-headline-medium md:pb-1.5 leading-tight text-on-surface md:text-on-surface-subtle md:group-hover:text-on-surface md:group-focus-visible:text-on-surface md:transition-colors md:duration-700 md:ease-[cubic-bezier(0.65,0,0.35,1)] min-w-0">
                                            {itemTitle}
                                          </span>
                                          <span className="font-mono text-label-large sm:text-body-small font-bold text-on-primary-subtle shrink-0 ml-auto md:ml-0 text-right">
                                            [{String(index + 1).padStart(2, '0')}]
                                          </span>
                                        </div>
                                      </div>

                                      {itemText && (
                                        <div className="col-span-1 md:col-span-8 w-full text-left opacity-100 md:opacity-0 md:group-hover:opacity-100 md:group-focus-visible:opacity-100 md:translate-x-3 md:group-hover:translate-x-0 md:transition-all md:duration-700 md:ease-[cubic-bezier(0.65,0,0.35,1)]">
                                          <div className="hidden md:flex items-center gap-extra-small mb-1.5 md:mb-2 justify-start">
                                            <span
                                              className="w-base h-base rounded-container-extra-small bg-primary inline-flex items-center justify-center shrink-0 text-on-primary-unstable"
                                              aria-hidden="true"
                                            >
                                              <svg
                                                viewBox="0 0 12 12"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className="w-extra-small h-extra-small"
                                                aria-hidden="true"
                                              >
                                                <line x1="6" y1="2" x2="6" y2="10" />
                                                <line x1="2" y1="6" x2="10" y2="6" />
                                              </svg>
                                            </span>
                                            <span className="font-palanquin-dark font-bold text-label-large sm:text-body-small uppercase tracking-wider text-on-surface-subtle group-hover:text-on-surface transition-colors duration-700 ease-[cubic-bezier(0.65,0,0.35,1)]">
                                              {itemTitle}
                                            </span>
                                          </div>
                                          <p className="font-palanquin text-body-large text-on-surface font-normal leading-relaxed text-left line-clamp-4 w-full">
                                            {itemText}
                                          </p>
                                        </div>
                                      )}
                                    </li>
                                    )
                                  })}
                                </ul>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )
                  })
              ) : (
                <div className="reveal-item opacity-0 text-center py-20 text-megaDarkGray">
                  {t('no-data-found', { defaultValue: 'No technology data found for the current language.' })}
                </div>
              )}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="radar-view"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.2, 1, 0.3, 1] }}
            className="hidden lg:flex flex-col gap-8 w-full relative z-0"
          >
            <InteractiveRadar technologies={mappedTechnologies} />
          </motion.div>
        )}
      </div>
      <ContactSection />
    </Layout>
  )
}

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`

export default IndexPage
